import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import './styles/luxury.css';
import './styles/admin.css';
import './styles/pdp.css';
import './styles/pillars.css';
import './styles/testimonials.css';
import './styles/policy.css';
import './styles/responsive.css';
import './styles/billing.css';
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import Craftsmanship from './components/Craftsmanship';
import Collection from './components/Collection';
import SuitCollection from './components/SuitCollection';
import Testimonials from './components/Testimonials';
import BespokeJourney from './components/BespokeJourney';
import ScrollToTop from './components/ScrollToTop';
import WhatsAppButton from './components/WhatsAppButton';
import Footer from './components/Footer';
import InquiryForm from './components/InquiryForm';
import FabricSelection from './components/FabricSelection';
import OrderTracking from './components/OrderTracking';
import DeliveryTracking from './components/DeliveryTracking';
import FabricDesignPage from './pages/FabricDesignPage';
import ContactPage from './pages/ContactPage';
import AboutUsPage from './pages/AboutUsPage';
import ArchivePage from './pages/ArchivePage';
import ShopPage from './pages/ShopPage';
import ComingSoon from './components/ComingSoon';
import ReadyToBuy from './components/ReadyToBuy';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import TrackPage from './pages/TrackPage';
import ProductDetailPage from './pages/ProductDetailPage';
import OrderProcessPage from './pages/OrderProcessPage';
import ReturnsExchangePage from './pages/ReturnsExchangePage';
import { ContentProvider, useContent, useSection, resolveAsset } from './contexts/ContentContext';
import SEO, { ORG_SCHEMA, websiteSchema } from './components/SEO';

// Coming Soon Mode Toggle - Set to 'true' to show Coming Soon page
const COMING_SOON_MODE = process.env.REACT_APP_COMING_SOON === 'true';

function HomePage({ 
  onExplore, 
  onInquire, 
  onStartJourney, 
  onOpenFabricSelection, 
  onOpenOrderTracking,
  onOpenDeliveryTracking 
}) {
  const hero = useSection('home.hero', {});
  const heroImage = resolveAsset(hero.image) || undefined;
  const description = hero.tagline
    ? `${hero.subtitle || ''}. ${hero.tagline.replace(/\n/g, ' ')}`.trim()
    : undefined;
  const homeSchema = {
    '@context': 'https://schema.org',
    '@graph': [ORG_SCHEMA, websiteSchema(typeof window !== 'undefined' ? window.location.origin : undefined)],
  };
  return (
    <>
      <SEO
        title="Silk & Sentiment"
        description={description}
        path="/"
        image={heroImage}
        schema={homeSchema}
      />
      <Hero onExplore={onExplore} />
      <Craftsmanship />
      <Collection onInquire={onInquire} />
      <SuitCollection onInquire={onInquire} />
      <ReadyToBuy />
      <Testimonials />
      <BespokeJourney 
        onStartJourney={onStartJourney}
        onOpenFabricSelection={onOpenFabricSelection}
        onOpenOrderTracking={onOpenOrderTracking}
        onOpenDeliveryTracking={onOpenDeliveryTracking}
      />
      <Footer />
    </>
  );
}

function App() {
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [isInquiryFormOpen, setIsInquiryFormOpen] = useState(false);
  const [isFabricSelectionOpen, setIsFabricSelectionOpen] = useState(false);
  const [isOrderTrackingOpen, setIsOrderTrackingOpen] = useState(false);
  const [isDeliveryTrackingOpen, setIsDeliveryTrackingOpen] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  useEffect(() => {
    // Check if coming soon mode is enabled
    if (COMING_SOON_MODE) {
      // Check for developer access via URL parameter or localStorage
      const urlParams = new URLSearchParams(window.location.search);
      const hasDevAccess = urlParams.get('preview') === 'silk2025' || 
                          localStorage.getItem('silk_dev_access') === 'true';
      
      if (hasDevAccess) {
        // Store dev access for future visits
        localStorage.setItem('silk_dev_access', 'true');
        setShowComingSoon(false);
      } else {
        setShowComingSoon(true);
      }
    }
  }, []);

  // Handler to unlock the full site from Coming Soon page
  const handleUnlockSite = () => {
    setShowComingSoon(false);
  };

  // Show Coming Soon page if enabled and no dev access
  if (showComingSoon) {
    return <ComingSoon onUnlock={handleUnlockSite} />;
  }

  const handleExplore = () => {
    const collectionSection = document.querySelector('#collection');
    if (collectionSection) {
      collectionSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInquire = (piece) => {
    setSelectedPiece(piece);
    setIsInquiryFormOpen(true);
  };

  const handleStartJourney = () => {
    setSelectedPiece(null);
    setIsInquiryFormOpen(true);
  };

  const handleCloseInquiryForm = () => {
    setIsInquiryFormOpen(false);
    setTimeout(() => setSelectedPiece(null), 300);
  };

  const handleOpenFabricSelection = () => {
    setIsFabricSelectionOpen(true);
  };

  const handleCloseFabricSelection = () => {
    setIsFabricSelectionOpen(false);
  };

  const handleFabricSelectionSubmit = (selections) => {
    console.log('Fabric Selection:', selections);
    setSelectedPiece({ 
      name: 'Custom Bespoke Creation',
      fabric: selections.fabric.name,
      embroidery: selections.embroidery.name
    });
    setIsInquiryFormOpen(true);
  };

  const handleOpenOrderTracking = () => {
    setIsOrderTrackingOpen(true);
  };

  const handleCloseOrderTracking = () => {
    setIsOrderTrackingOpen(false);
  };

  const handleOpenDeliveryTracking = () => {
    setIsDeliveryTrackingOpen(true);
  };

  const handleCloseDeliveryTracking = () => {
    setIsDeliveryTrackingOpen(false);
  };

  return (
    <HelmetProvider>
      <ContentProvider>
        <Router>
          <AppShell
            selectedPiece={selectedPiece}
            isInquiryFormOpen={isInquiryFormOpen}
            isFabricSelectionOpen={isFabricSelectionOpen}
            isOrderTrackingOpen={isOrderTrackingOpen}
            isDeliveryTrackingOpen={isDeliveryTrackingOpen}
            handleExplore={handleExplore}
            handleInquire={handleInquire}
            handleStartJourney={handleStartJourney}
            handleCloseInquiryForm={handleCloseInquiryForm}
            handleOpenFabricSelection={handleOpenFabricSelection}
            handleCloseFabricSelection={handleCloseFabricSelection}
            handleFabricSelectionSubmit={handleFabricSelectionSubmit}
            handleOpenOrderTracking={handleOpenOrderTracking}
            handleCloseOrderTracking={handleCloseOrderTracking}
            handleOpenDeliveryTracking={handleOpenDeliveryTracking}
            handleCloseDeliveryTracking={handleCloseDeliveryTracking}
          />
        </Router>
      </ContentProvider>
    </HelmetProvider>
  );
}

function AppShell({
  selectedPiece,
  isInquiryFormOpen,
  isFabricSelectionOpen,
  isOrderTrackingOpen,
  isDeliveryTrackingOpen,
  handleExplore,
  handleInquire,
  handleStartJourney,
  handleCloseInquiryForm,
  handleOpenFabricSelection,
  handleCloseFabricSelection,
  handleFabricSelectionSubmit,
  handleOpenOrderTracking,
  handleCloseOrderTracking,
  handleOpenDeliveryTracking,
  handleCloseDeliveryTracking,
}) {
  const location = useLocation();
  const { reload: reloadContent } = useContent();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isTrackRoute = location.pathname.startsWith('/track');
  const isChromeHidden = isAdminRoute || isTrackRoute;

  // When the user leaves the admin area and lands on any public route,
  // refresh the in-memory content so any edits saved in admin are reflected
  // immediately without a hard refresh.
  const wasAdminRef = React.useRef(isAdminRoute);
  React.useEffect(() => {
    if (wasAdminRef.current && !isAdminRoute) {
      reloadContent();
    }
    wasAdminRef.current = isAdminRoute;
  }, [isAdminRoute, reloadContent]);

  return (
    <div className="App" style={{ backgroundColor: 'var(--bg-primary)' }}>
      <ScrollToTop />
      {!isChromeHidden && <Navigation />}
      <Routes>
        <Route
          path="/"
          element={
            <HomePage
              onExplore={handleExplore}
              onInquire={handleInquire}
              onStartJourney={handleStartJourney}
              onOpenFabricSelection={handleOpenFabricSelection}
              onOpenOrderTracking={handleOpenOrderTracking}
              onOpenDeliveryTracking={handleOpenDeliveryTracking}
            />
          }
        />
        <Route path="/design-studio" element={<FabricDesignPage />} />
        <Route path="/archive" element={<><ArchivePage /><Footer /></>} />
        <Route path="/archive/:id" element={<ProductDetailPage />} />
        <Route path="/shop" element={<><ShopPage /><Footer /></>} />
        <Route path="/shop/:id" element={<ProductDetailPage />} />
        <Route path="/suits" element={<Navigate to="/archive?type=suit" replace />} />
        <Route path="/suits/:id" element={<ProductDetailPage />} />
        <Route path="/about" element={<><AboutUsPage /><Footer /></>} />
        <Route path="/contact" element={<><ContactPage /><Footer /></>} />
        <Route path="/order-process" element={<><OrderProcessPage /><Footer /></>} />
        <Route path="/returns-exchange" element={<><ReturnsExchangePage /><Footer /></>} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
      </Routes>
      {!isChromeHidden && (
        <>
          <WhatsAppButton selectedPiece={selectedPiece} />
          <InquiryForm
            isOpen={isInquiryFormOpen}
            onClose={handleCloseInquiryForm}
            selectedPiece={selectedPiece}
          />
          <FabricSelection
            isOpen={isFabricSelectionOpen}
            onClose={handleCloseFabricSelection}
            onSubmit={handleFabricSelectionSubmit}
          />
          <OrderTracking
            isOpen={isOrderTrackingOpen}
            onClose={handleCloseOrderTracking}
          />
          <DeliveryTracking
            isOpen={isDeliveryTrackingOpen}
            onClose={handleCloseDeliveryTracking}
          />
        </>
      )}
    </div>
  );
}

export default App;
